import { useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  FaCalendar,
  FaChevronDown,
  FaChevronRight,
  FaStoreAlt,
  FaTrashAlt,
} from 'react-icons/fa';
import { Badge } from 'reactstrap';
import i18n from '@/utils/i18n';
import { cn } from '@/lib/utils';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Badge as ShadcnBadge } from '@/components/ui/badge';
import { getImageUrl } from '@/utils/imageUrl';
import { getColorForTier } from '@/utils/color';
import { formatShortDate } from '@/utils/formatter';
import StoreStatusBadge from '@/views/MerchantComponents/StoreStatusBadge';
import { Routes } from '@/utils/routes';
import type {
  PartnerAccountNodeCoreFragment,
  SearchAdminV2Query,
} from '@/graphql/queries/searchAdminV2.generated';

// The fragment itself doesn't expose `children` (codegen expands them inline at
// the query level); keep a recursive generic that covers all 3 depth levels.
type HierarchyNode = PartnerAccountNodeCoreFragment & {
  children?: HierarchyNode[];
};

type OrphanedStoreMatch =
  SearchAdminV2Query['searchAdminV2']['orphaned_stores'][number];
type OrphanedEventMatch =
  SearchAdminV2Query['searchAdminV2']['orphaned_events'][number];

type SortedItem =
  | { kind: 'account'; node: HierarchyNode; score: number }
  | { kind: 'orphaned-store'; match: OrphanedStoreMatch; score: number }
  | { kind: 'orphaned-event'; match: OrphanedEventMatch; score: number };

type PartnerHierarchyResultsProps = {
  nodes: HierarchyNode[];
  orphanedStores?: OrphanedStoreMatch[];
  orphanedEvents?: OrphanedEventMatch[];
  selectedStoreId?: string;
  showStoreIdSuffix?: boolean;
};

export function PartnerHierarchyResults(props: PartnerHierarchyResultsProps) {
  const location = useLocation();
  const searchSuffix = location.search; // e.g. "?q=foo&deleted=1"

  const sortedItems = useMemo(() => {
    const items: SortedItem[] = [];
    for (const node of props.nodes) {
      items.push({ kind: 'account', node, score: node.score });
    }
    for (const match of props.orphanedStores ?? []) {
      items.push({ kind: 'orphaned-store', match, score: match.score });
    }
    for (const match of props.orphanedEvents ?? []) {
      items.push({ kind: 'orphaned-event', match, score: match.score });
    }
    items.sort((a, b) => b.score - a.score);
    return items;
  }, [props.nodes, props.orphanedStores, props.orphanedEvents]);

  return (
    <div className="tw:flex tw:flex-col tw:gap-1 tw:py-2">
      {sortedItems.map((item) => {
        if (item.kind === 'account') {
          return (
            <AccountNode
              key={item.node.event_account.id}
              node={item.node}
              depth={0}
              selectedStoreId={props.selectedStoreId}
              showStoreIdSuffix={props.showStoreIdSuffix}
              searchSuffix={searchSuffix}
            />
          );
        }
        if (item.kind === 'orphaned-store') {
          return (
            <OrphanedStoreRow
              key={item.match.store.id}
              match={item.match}
              isSelected={props.selectedStoreId === item.match.store.id}
              showStoreIdSuffix={props.showStoreIdSuffix}
              searchSuffix={searchSuffix}
            />
          );
        }
        return (
          <OrphanedEventRow key={item.match.event.id} match={item.match} />
        );
      })}
    </div>
  );
}

type NodeSelectionProps = {
  selectedStoreId?: string;
  showStoreIdSuffix?: boolean;
  searchSuffix: string;
};

function AccountNode({
  node,
  depth,
  ...selection
}: { node: HierarchyNode; depth: number } & NodeSelectionProps) {
  const account = node.event_account;
  const hasChildren =
    node.stores.length > 0 ||
    node.events.length > 0 ||
    (node.children?.length ?? 0) > 0;

  const [open, setOpen] = useState(true);

  return (
    <div>
      <Collapsible open={open} onOpenChange={setOpen}>
        <div
          className={cn(
            'tw:flex tw:flex-row tw:items-center tw:gap-2 tw:rounded tw:px-2 tw:py-1',
            account.is_deleted && 'tw:opacity-40',
          )}
        >
          {hasChildren ? (
            <CollapsibleTrigger
              className="tw:flex tw:size-5 tw:items-center tw:justify-center tw:rounded tw:text-muted-foreground tw:hover:bg-muted"
              aria-label={open ? 'Collapse' : 'Expand'}
            >
              {open ? (
                <FaChevronDown className="tw:size-3" />
              ) : (
                <FaChevronRight className="tw:size-3" />
              )}
            </CollapsibleTrigger>
          ) : (
            <span className="tw:size-5" />
          )}
          <FaStoreAlt className="tw:size-4 tw:text-muted-foreground" />
          <Link
            to={Routes.partnerAccountDetail(account.id)}
            target="_blank"
            rel="noopener noreferrer"
            className="tw:flex tw:flex-row tw:items-center tw:gap-2 tw:font-semibold tw:no-underline tw:hover:underline"
          >
            <span>{account.name || account.company_name || account.id}</span>
            {account.is_deleted && <DeletedIndicator />}
          </Link>
        </div>

        <CollapsibleContent>
          <div
            className={cn(
              'tw:ml-2 tw:flex tw:flex-col tw:gap-1 tw:border-l tw:border-border tw:pl-3',
            )}
          >
            {node.stores.map((storeMatch) => (
              <StoreRow
                key={storeMatch.store.id}
                store={storeMatch.store}
                isSelected={selection.selectedStoreId === storeMatch.store.id}
                showStoreIdSuffix={selection.showStoreIdSuffix}
                searchSuffix={selection.searchSuffix}
              />
            ))}
            {node.events.map((eventMatch) => (
              <EventRow key={eventMatch.event.id} event={eventMatch.event} />
            ))}
            {node.children?.map((child) => (
              <AccountNode
                key={child.event_account.id}
                node={child}
                depth={depth + 1}
                {...selection}
              />
            ))}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}

type StoreNode = PartnerAccountNodeCoreFragment['stores'][number]['store'];

function StoreRow({
  store,
  isSelected,
  showStoreIdSuffix,
  searchSuffix,
}: {
  store: StoreNode;
  isSelected: boolean;
  showStoreIdSuffix?: boolean;
  searchSuffix: string;
}) {
  const removed = !!store.permanently_removed;
  return (
    <Link
      to={`${Routes.partnerStore(store.id)}${searchSuffix}`}
      className={cn(
        'tw:flex tw:flex-row tw:items-start tw:gap-2 tw:rounded tw:px-2 tw:py-1 tw:text-sm tw:no-underline tw:text-inherit tw:hover:bg-muted/50',
        removed && 'tw:opacity-40',
        isSelected && 'tw:bg-muted',
      )}
    >
      <Avatar className="tw:size-7 tw:mt-0.5 tw:shrink-0">
        <AvatarImage src={getImageUrl(30, store.banner) as string} />
        <AvatarFallback>
          <FaStoreAlt className="tw:size-3 tw:text-muted-foreground" />
        </AvatarFallback>
      </Avatar>
      <div className="tw:flex tw:flex-col tw:gap-0.5">
        <div className="tw:flex tw:flex-row tw:flex-wrap tw:items-center tw:gap-x-2 tw:gap-y-1">
          <span className="tw:font-medium">{store.name}</span>
          {removed && <DeletedIndicator />}
          {(store.revenue_tier || store.tier) && (
            <Badge
              style={{
                background: getColorForTier(
                  (store.revenue_tier || store.tier) as string,
                ),
              }}
              pill
            >
              {store.revenue_tier || store.tier}
            </Badge>
          )}
          {store.published && (
            <Badge color="success" pill>
              {i18n.t('PartnerSearch:published')}
            </Badge>
          )}
          {(store.available_soon || store.coming_soon) && (
            <Badge pill>{i18n.t('PartnerSearch:comingSoon')}</Badge>
          )}
          <StoreStatusBadge
            contracted_date={store.contracted_date}
            onboarded_date={store.onboarded_date}
            contract_end_date={store.contract_end_date}
          />
          {store.inkassokennzeichen && (
            <Badge color="warning" pill>
              Inkasso
            </Badge>
          )}
        </div>
        <div className="tw:text-muted-foreground tw:text-xs">
          {store.address && (
            <>
              {store.address.streetname} {store.address.streetno},{' '}
              {store.address.zip} {store.address.city}
            </>
          )}
          {showStoreIdSuffix && <span className="tw:ml-2">{store.id}</span>}
        </div>
      </div>
    </Link>
  );
}

type EventNode = PartnerAccountNodeCoreFragment['events'][number]['event'];

function EventRow({ event }: { event: EventNode }) {
  const removed = !!event.is_deleted;
  const dateRange = [
    event.start_date && formatShortDate(event.start_date),
    event.end_date && formatShortDate(event.end_date),
  ]
    .filter(Boolean)
    .join(' – ');
  return (
    <Link
      to={Routes.eventDetail(event.id)}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(
        'tw:flex tw:flex-row tw:items-center tw:gap-2 tw:rounded tw:px-2 tw:py-1 tw:text-sm tw:no-underline tw:text-inherit tw:hover:bg-muted/50',
        removed && 'tw:opacity-40',
      )}
    >
      <FaCalendar className="tw:size-4 tw:text-muted-foreground tw:shrink-0" />
      <div className="tw:flex tw:flex-row tw:flex-wrap tw:items-center tw:gap-x-2">
        <span className="tw:font-medium">{event.name || event.id}</span>
        {removed && <DeletedIndicator />}
        <span className="tw:text-muted-foreground tw:text-xs">
          {event.type}
        </span>
        {dateRange && (
          <span className="tw:text-muted-foreground tw:text-xs">
            {dateRange}
          </span>
        )}
      </div>
    </Link>
  );
}

function NoAccountBadge() {
  return (
    <ShadcnBadge
      variant="outline"
      className="tw:text-[10px] tw:py-0 tw:text-muted-foreground"
    >
      {i18n.t('PartnerSearch:noAccount')}
    </ShadcnBadge>
  );
}

function OrphanedStoreRow({
  match,
  isSelected,
  showStoreIdSuffix,
  searchSuffix,
}: {
  match: OrphanedStoreMatch;
  isSelected: boolean;
  showStoreIdSuffix?: boolean;
  searchSuffix: string;
}) {
  const store = match.store;
  const removed = !!store.permanently_removed;
  return (
    <Link
      to={`${Routes.partnerStore(store.id)}${searchSuffix}`}
      className={cn(
        'tw:flex tw:flex-row tw:items-start tw:gap-2 tw:rounded tw:px-2 tw:py-1 tw:text-sm tw:no-underline tw:text-inherit tw:hover:bg-muted/50',
        removed && 'tw:opacity-40',
        isSelected && 'tw:bg-muted',
      )}
    >
      <span className="tw:size-5 tw:shrink-0" />
      <Avatar className="tw:size-7 tw:mt-0.5 tw:shrink-0">
        <AvatarImage src={getImageUrl(30, store.banner) as string} />
        <AvatarFallback>
          <FaStoreAlt className="tw:size-3 tw:text-muted-foreground" />
        </AvatarFallback>
      </Avatar>
      <div className="tw:flex tw:flex-col tw:gap-0.5">
        <div className="tw:flex tw:flex-row tw:flex-wrap tw:items-center tw:gap-x-2 tw:gap-y-1">
          <span className="tw:font-medium">{store.name}</span>
          <NoAccountBadge />
          {removed && <DeletedIndicator />}
          {(store.revenue_tier || store.tier) && (
            <Badge
              style={{
                background: getColorForTier(
                  (store.revenue_tier || store.tier) as string,
                ),
              }}
              pill
            >
              {store.revenue_tier || store.tier}
            </Badge>
          )}
          {store.published && (
            <Badge color="success" pill>
              {i18n.t('PartnerSearch:published')}
            </Badge>
          )}
          {(store.available_soon || store.coming_soon) && (
            <Badge pill>{i18n.t('PartnerSearch:comingSoon')}</Badge>
          )}
          <StoreStatusBadge
            contracted_date={store.contracted_date}
            onboarded_date={store.onboarded_date}
            contract_end_date={store.contract_end_date}
          />
          {store.inkassokennzeichen && (
            <Badge color="warning" pill>
              Inkasso
            </Badge>
          )}
        </div>
        <div className="tw:text-muted-foreground tw:text-xs">
          {store.address && (
            <>
              {store.address.streetname} {store.address.streetno},{' '}
              {store.address.zip} {store.address.city}
            </>
          )}
          {showStoreIdSuffix && <span className="tw:ml-2">{store.id}</span>}
        </div>
      </div>
    </Link>
  );
}

function OrphanedEventRow({ match }: { match: OrphanedEventMatch }) {
  const event = match.event;
  const removed = !!event.is_deleted;
  const dateRange = [
    event.start_date && formatShortDate(event.start_date),
    event.end_date && formatShortDate(event.end_date),
  ]
    .filter(Boolean)
    .join(' – ');
  return (
    <Link
      to={Routes.eventDetail(event.id)}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(
        'tw:flex tw:flex-row tw:items-center tw:gap-2 tw:rounded tw:px-2 tw:py-1 tw:text-sm tw:no-underline tw:text-inherit tw:hover:bg-muted/50',
        removed && 'tw:opacity-40',
      )}
    >
      <span className="tw:size-5 tw:shrink-0" />
      <FaCalendar className="tw:size-4 tw:text-muted-foreground tw:shrink-0" />
      <div className="tw:flex tw:flex-row tw:flex-wrap tw:items-center tw:gap-x-2">
        <span className="tw:font-medium">{event.name || event.id}</span>
        <NoAccountBadge />
        {removed && <DeletedIndicator />}
        <span className="tw:text-muted-foreground tw:text-xs">
          {event.type}
        </span>
        {dateRange && (
          <span className="tw:text-muted-foreground tw:text-xs">
            {dateRange}
          </span>
        )}
      </div>
    </Link>
  );
}

function DeletedIndicator() {
  return (
    <ShadcnBadge
      variant="destructive"
      className="tw:gap-1 tw:text-[10px] tw:py-0"
    >
      <FaTrashAlt className="tw:size-2.5" />
      {i18n.t('PartnerSearch:deleted')}
    </ShadcnBadge>
  );
}
